<template>
  <section>
    <p class="tw-mb-4">{{ t("msp-admin-account") }}</p>
    <div class="tw-mb-3 tw-grid tw-grid-cols-2 tw-gap-5">
      <div class="w-full w-2/4 px-2 mb-2 md:mb-0 tw-col-span-1">
        <cl-form-group>
          <cl-form-label :label-for="'name'">
            {{ t("full-name-label") }}
          </cl-form-label>
          <cl-form-input
            :id="'name'"
            v-model:value="form.name"
            :name="'name'"
            :type="'text'"
            :placeholder="'Full Name'"
            :state="!v$.name.$dirty ? undefined : !v$.name.$error"
            :data-test-id="'add-msp-input-name'"
            @blur="handleBlur('name')"
            @input="v$.name.$reset()"
          />
          <span v-if="!v$.name.$error" class="tw-text-sm">
            {{ t("customer-name-hint") }}
          </span>
          <span
            v-if="v$.name.$error"
            class="tw-text-system-error-500 tw-text-sm"
          >
            <span v-if="!v$.name.required.$response">
              {{ t("required-field") }}
            </span>
            <span
              v-else-if="
                !v$.name.maxLength.$response || !v$.name.minLength.$response
              "
            >
              {{ t("customer-name-validation") }}
            </span>
          </span>
        </cl-form-group>
      </div>
      <div class="w-full md:w-1/2 px-2">
        <cl-form-group>
          <cl-form-label :label-for="'email'">
            {{ t("email-title") }}
          </cl-form-label>

          <cl-form-input
            :id="'email'"
            v-model:value="form.email"
            :name="'email'"
            :type="'text'"
            :data-test-id="`add-msp-admin-email`"
            :placeholder="'john@example.com'"
            :state="!v$.email.$dirty ? undefined : !v$.email.$error"
            @blur="
              () => {
                handleBlur('email');
                isEmailTaken = false;
                isEmailRecordWrong = false;
              }
            "
            @input="
              () => {
                v$.email.$reset();
                isEmailTaken = false;
                isEmailRecordWrong = false;
              }
            "
          />
          <span
            v-if="!v$.email.$error && !isEmailTaken && !isEmailRecordWrong"
            class="tw-text-sm"
          >
            {{ t("email-hint") }}
          </span>
          <span v-if="isEmailTaken" class="tw-text-system-error-500 tw-text-sm">
            {{ t("email-in-use") }}
          </span>
          <span
            v-if="isEmailRecordWrong"
            class="tw-text-system-error-500 tw-text-sm"
          >
            {{ t("valid-email-format") }}
          </span>
          <span
            v-if="v$.email.$error && !isEmailTaken && !isEmailRecordWrong"
            class="tw-text-system-error-500 tw-text-sm"
          >
            <span v-if="!v$.email.required.$response">
              {{ t("required-field") }}
            </span>
            <span v-else-if="!v$.email.email.$response">
              {{ t("valid-email-format") }}
            </span>
          </span>
        </cl-form-group>
      </div>
    </div>
    <div class="tw-mb-3 tw-grid tw-grid-cols-2 tw-gap-5">
      <div class="w-full w-2/4 px-2 mb-2 md:mb-0 tw-col-span-1">
        <cl-form-group>
          <cl-form-label :label-for="'password'">
            {{ t("password-label") }}
          </cl-form-label>
          <t-password-input
            :id="'password'"
            v-model:value="form.password"
            :name="'password'"
            :placeholder="t('password-placeholder')"
            :data-test-id="`add-msp-admin-password`"
            :state="!v$.password.$dirty ? undefined : !v$.password.$error"
            @blur="handleBlur('password')"
            @input="v$.password.$reset()"
          />
          <span v-if="!v$.password.$error" class="tw-text-sm">
            {{ t("password-validation") }}
          </span>
          <span
            v-if="v$.password.$error"
            class="tw-text-system-error-500 tw-text-sm"
          >
            <span v-if="!v$.password.required.$response">
              {{ t("required-field") }}
            </span>
            <span
              v-else-if="
                !v$.password.strongPassword.$response ||
                !v$.password.maxLength.$response ||
                !v$.password.minLength.$response
              "
            >
              {{ t("password-validation") }}
            </span>
          </span>
        </cl-form-group>
      </div>
      <div class="w-full md:w-1/2 px-2">
        <cl-form-group>
          <cl-form-label :label-for="'confirm_password'">
            {{ t("confirm-password-label") }}
          </cl-form-label>
          <t-password-input
            :id="'confirm_password'"
            v-model:value="form.confirm_password"
            :name="'password'"
            :data-test-id="`msp-add-admin-confirm-password`"
            :placeholder="t('confirm-password-placeholder')"
            :state="
              !v$.confirm_password.$dirty
                ? undefined
                : !v$.confirm_password.$error
            "
            @blur="handleBlur('confirm_password')"
            @input="v$.confirm_password.$reset()"
          />
          <!--              sameAsPassword required-->
          <span
            v-if="v$.confirm_password.$error"
            class="tw-text-system-error-500 tw-text-sm"
          >
            <span v-if="!v$.confirm_password.required.$response">
              {{ t("required-field") }}
            </span>
            <span v-else-if="!v$.confirm_password.sameAsPassword.$response">
              {{ t("confirm-password-validation") }}
            </span>
          </span>
        </cl-form-group>
      </div>
    </div>
  </section>
</template>
<script lang="ts">
type Form = {
  name: string;
  email: string;
  password: string;
  confirm_password: string;
  roles: string[];
};

interface Props {
  form: Form;
  isEmailTaken: boolean;
  isEmailRecordWrong: boolean;
  triggerEmailValidation?: number;
}
</script>
<script setup lang="ts">
import { reactive, computed, watch, watchEffect, ref } from "vue";
import { useI18n } from "vue-i18n";
import TPasswordInput from "@/components/TPasswordInput/TPasswordInput.vue";
import { useVuelidate } from "@vuelidate/core";
import {
  required,
  sameAs,
  email,
  minLength,
  maxLength,
} from "@vuelidate/validators";
import { STRONG_PASSWORD } from "@/constants";

const { t } = useI18n();
const props = defineProps<Props>();
const form = reactive<Form>({ ...props.form });
const isEmailTaken = ref<boolean>(props.isEmailTaken);
const isEmailRecordWrong = ref<boolean>(props.isEmailRecordWrong);

const emit = defineEmits<{
  (e: "update:form", form: Form): void;
  (e: "formValidation", isValid: boolean): void;
  (e: "update:isEmailTaken", isEmailTaken: boolean): void;
  (e: "update:isEmailRecordWrong", isEmailRecordWrong: boolean): void;
}>();

const rules = {
  name: { required, minLength: minLength(3), maxLength: maxLength(100) },
  email: { required, email, maxLength: maxLength(200) },
  password: {
    required,
    minLength: minLength(12),
    maxLength: maxLength(72),
    strongPassword: (password: string) => STRONG_PASSWORD.test(password),
  },
  confirm_password: {
    required,
    sameAsPassword: sameAs(computed(() => form.password)),
  },
};

const v$ = useVuelidate(rules, form);

const handleBlur = async (field: string) => {
  const isValid = await v$.value[field].$validate();
  if (!isValid) {
    return;
  }
};

watch(form, (newValue) => {
  emit("update:form", newValue);
});

watch(isEmailTaken, (newValue) => {
  emit("update:isEmailTaken", newValue);
});

watch(isEmailRecordWrong, (newValue) => {
  emit("update:isEmailRecordWrong", newValue);
});

watchEffect(() => {
  emit("formValidation", v$.value.$invalid);
});

watch(
  () => props.triggerEmailValidation,
  () => {
    if (props.triggerEmailValidation && props.triggerEmailValidation > 0) {
      v$.value.email.$touch();
    }
  },
  { immediate: true },
);
</script>
